import graphql__683fda2419f48b73e033 from "./__generated__/AppOrganizationSlugDestinationInputInputQuery.graphql.ts";
import graphql__3b45149240917255aa81 from "./__generated__/AppOrganizationSlugDestinationInputTransferMutation.graphql.ts";import { Alert, Button, icons, Input } from '@portal/components'
import { graphql } from 'babel-plugin-relay/macro'
import debounce from 'lodash/debounce'
import React, { useRef, useState } from 'react'
import { fetchQuery } from 'react-relay'
import { useMutation, useRelayEnvironment } from 'react-relay/hooks'

import { AppOrganizationSelectDestinationInputTransferMutation } from './__generated__/AppOrganizationSelectDestinationInputTransferMutation.graphql'
import { AppOrganizationSlugDestinationInputInputQuery } from './__generated__/AppOrganizationSlugDestinationInputInputQuery.graphql'
import TransferAppConfirmationModal from './TransferAppConfirmationModal'

const query = graphql__683fda2419f48b73e033

const transferMutation = graphql__3b45149240917255aa81

const { CheckCircleSolid, Spinner } = icons

type Props = {
  appId: string
}

const AppOrganizationSlugDestinationInput: React.FC<Props> = ({ appId }) => {
  const environment = useRelayEnvironment()
  const [transferApp, isTransferringApp] =
    useMutation<AppOrganizationSelectDestinationInputTransferMutation>(
      transferMutation,
    )
  const [value, setValue] = useState('')
  const [destinationOrganizationId, setDestinationOrganizationId] =
    useState<string>()
  const [isVerifyingSlug, setIsVerifyingSlug] = useState(false)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [transferError, setTransferError] = useState<Error>()
  const debouncedOrganizationCheck = useRef(
    debounce((search) => {
      fetchQuery<AppOrganizationSlugDestinationInputInputQuery>(
        environment,
        query,
        { slug: search },
        { networkCacheConfig: { force: true } },
      )
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .toPromise()
        .then((result) => {
          setDestinationOrganizationId(result?.viewer.organization?.id)
          setIsVerifyingSlug(false)
        })
        .catch(() => {
          setIsVerifyingSlug(false)
        })
      // Can't use finally here to call setIsVerifyingSlug(false) since there's some incompatibility issues with react-testing-library
    }, 1000),
  )

  const handleSlugChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setDestinationOrganizationId(undefined)
    setValue(evt.target.value)
    if (evt.target.value) {
      setIsVerifyingSlug(true)
      debouncedOrganizationCheck.current(evt.target.value)
    }
  }

  const handleTransferClicked = () => {
    setTransferError(undefined)
    setIsConfirmationOpen(true)
  }

  const handleConfirmationClosed = (confirmTransfer: boolean) => {
    if (confirmTransfer && destinationOrganizationId) {
      transferApp({
        variables: { appId, organizationId: destinationOrganizationId },
        onCompleted: () => {
          setIsConfirmationOpen(false)
        },
        onError: (err) => {
          setTransferError(err)
        },
      })
    } else {
      setIsConfirmationOpen(false)
    }
  }

  const isInvalid = !isVerifyingSlug && !!value && !destinationOrganizationId

  return (
    <>
      {!!transferError && (
        <Alert
          autoDismiss={false}
          variant="danger"
        >{`Could not create the app transfer request: ${transferError.message}`}</Alert>
      )}
      <TransferAppConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={handleConfirmationClosed}
        appId={appId}
        destination={value}
        isProcessing={isTransferringApp}
      />
      <div className="w-full">
        <Input
          autoComplete="off"
          trailingIcon={
            destinationOrganizationId ? (
              <CheckCircleSolid
                data-testid="verifiedCheckmark"
                className="h-4 w-4 text-fill-success"
              />
            ) : isVerifyingSlug ? (
              <Spinner
                data-testid="verifyingOrganization"
                className="h-4 w-4 animate-spin text-foreground-normal"
              />
            ) : null
          }
          id="organizationSlug"
          label="Organization slug"
          description="The slug of the organization in the portal"
          onChange={handleSlugChanged}
          placeholder="portal-slug-for-organization"
          value={value}
          errorMessage={
            isInvalid
              ? 'No organization was found with the provided slug'
              : undefined
          }
        />
      </div>
      <Button
        disabled={!destinationOrganizationId}
        onClick={handleTransferClicked}
        className="mt-8"
        variant="critical"
      >
        Transfer
      </Button>
    </>
  )
}

export default AppOrganizationSlugDestinationInput
